import React, { useEffect, useRef, useState } from 'react';
import {
  Box,
  Button,
  FormControl,
  FormHelperText,
  Grid2,
  Stack,
  Typography,
} from '@mui/material';
import { Field } from 'react-final-form';

const requiredValidation = (value) => (value || value === 0 ? undefined : 'Erforderlich');

const DEFAULT_ALLOWED_CONTENT_TYPES = ['image/png', 'image/jpeg', 'application/pdf'];
const DEFAULT_MAX_FILE_SIZE_MB = 5;

const composeValidators = (...validators) => (value) => validators.reduce((error, validator) => error || validator(value), undefined);

const getAllowedContentTypes = (allowedContentTypes) => {
  if (Array.isArray(allowedContentTypes) && allowedContentTypes.length > 0) {
    const normalized = allowedContentTypes.filter(
      (contentType) => typeof contentType === 'string' && contentType.trim().length > 0,
    );
    if (normalized.length > 0) return normalized;
  }
  return DEFAULT_ALLOWED_CONTENT_TYPES;
};

const getMaxFileSizeMb = (maxFileSizeMb) => {
  const parsed = Number(maxFileSizeMb);
  return Number.isFinite(parsed) && parsed > 0 ? parsed : DEFAULT_MAX_FILE_SIZE_MB;
};

const formatFileSize = (sizeInBytes) => {
  if (!Number.isFinite(sizeInBytes) || sizeInBytes < 0) return '';
  const mb = sizeInBytes / (1024 * 1024);
  if (mb >= 1) return `${mb.toFixed(mb >= 10 ? 0 : 1)} MB`;
  const kb = sizeInBytes / 1024;
  return `${kb.toFixed(kb >= 10 ? 0 : 1)} KB`;
};

function FileInputFieldContent({
  input,
  meta,
  gridSize,
  label,
  required,
  helperText,
  disabled,
  allowedContentTypes,
  maxFileSizeMb,
}) {
  const fileInputRef = useRef(null);
  const [fileError, setFileError] = useState('');

  const normalizedAllowedContentTypes = getAllowedContentTypes(allowedContentTypes);
  const normalizedMaxFileSizeMb = getMaxFileSizeMb(maxFileSizeMb);
  const fileAccept = normalizedAllowedContentTypes.join(',');
  const selectedFile = input.value instanceof File ? input.value : null;
  const storedFileValue = !selectedFile && input.value ? input.value : null;
  const hasExistingUpload = Boolean(selectedFile || storedFileValue);
  const labelContent = required ? `${label} *` : label;
  const displayFileName = selectedFile?.name
    || storedFileValue?.name
    || storedFileValue?.filename
    || storedFileValue?.originalFilename
    || null;
  const displayFileSize = selectedFile?.size || storedFileValue?.size || null;

  useEffect(() => {
    setFileError('');
    if (fileInputRef.current) {
      fileInputRef.current.value = '';
    }
  }, [selectedFile, storedFileValue]);

  const handleFilePickerOpen = () => {
    if (disabled) return;
    setFileError('');
    const fileInput = fileInputRef.current;
    if (fileInput) {
      fileInput.value = '';
      fileInput.click();
    }
  };

  const handleFileChange = (event) => {
    const fileInput = event.target;
    const nextFile = fileInput.files?.[0];
    if (!nextFile) return;

    if (!normalizedAllowedContentTypes.includes(nextFile.type)) {
      setFileError(`Dateityp nicht erlaubt. Erlaubt: ${normalizedAllowedContentTypes.join(', ')}`);
      fileInput.value = '';
      return;
    }

    const maxBytes = normalizedMaxFileSizeMb * 1024 * 1024;
    if (nextFile.size > maxBytes) {
      setFileError(`Datei zu groß. Maximal ${normalizedMaxFileSizeMb} MB erlaubt.`);
      fileInput.value = '';
      return;
    }

    setFileError('');
    input.onChange(nextFile);
    fileInput.value = '';
  };

  const handleFileRemove = () => {
    setFileError('');
    input.onChange(null);
    if (fileInputRef.current) {
      fileInputRef.current.value = '';
    }
  };

  const hasError = Boolean(fileError || (meta.touched && (meta.error || meta.submitError)));
  const helperMessage = fileError
    || (meta.touched && (meta.error || meta.submitError))
    || helperText
    || `Erlaubte Dateitypen: ${normalizedAllowedContentTypes.join(', ')}. Maximal ${normalizedMaxFileSizeMb} MB.`;

  return (
    <Grid2 size={gridSize}>
      <FormControl fullWidth error={hasError}>
        {label && (
          <Typography variant="body2" sx={{ mb: 1, fontWeight: 500 }}>
            {labelContent}
          </Typography>
        )}
        <input
          ref={fileInputRef}
          type="file"
          accept={fileAccept}
          disabled={disabled}
          onChange={handleFileChange}
          style={{ display: 'none' }}
        />

        <Box
          onClick={!disabled && !hasExistingUpload ? handleFilePickerOpen : undefined}
          sx={{
            display: 'flex',
            alignItems: 'center',
            justifyContent: 'space-between',
            border: '1px solid',
            borderColor: hasError ? 'error.main' : 'divider',
            borderRadius: 1,
            px: 1.5,
            py: 1,
            gap: 1,
            cursor: !disabled && !hasExistingUpload ? 'pointer' : 'default',
            bgcolor: disabled ? 'action.disabledBackground' : 'transparent',
            '&:hover': !disabled && !hasExistingUpload
              ? { borderColor: 'text.primary' }
              : {},
          }}
        >
          {/* ── File info ── */}
          <Box sx={{ flex: 1, minWidth: 0 }}>
            {hasExistingUpload ? (
              <>
                <Typography variant="body2" sx={{ wordBreak: 'break-word' }}>
                  {displayFileName ?? 'Datei hochgeladen'}
                </Typography>
                {displayFileSize && (
                  <Typography variant="caption" color="text.secondary">
                    {formatFileSize(displayFileSize)}
                  </Typography>
                )}
              </>
            ) : (
              <Typography variant="body2" color="text.secondary">
                Keine Datei ausgewählt
              </Typography>
            )}
          </Box>

          {/* ── Actions ── */}
          <Stack direction="row" spacing={0.5} flexShrink={0}>
            <Button
              variant="outlined"
              size="small"
              type="button"
              onClick={(e) => { e.stopPropagation(); handleFilePickerOpen(); }}
              disabled={disabled}
              sx={{ whiteSpace: 'nowrap' }}
            >
              {hasExistingUpload ? 'Ersetzen' : 'Durchsuchen'}
            </Button>
            {hasExistingUpload && (
              <Button
                variant="text"
                color="error"
                size="small"
                type="button"
                onClick={(e) => { e.stopPropagation(); handleFileRemove(); }}
                disabled={disabled}
              >
                Entfernen
              </Button>
            )}
          </Stack>
        </Box>

        {helperMessage && (
          <FormHelperText>{helperMessage}</FormHelperText>
        )}
      </FormControl>
    </Grid2>
  );
}

export default function FileInputField({
  name,
  label,
  gridSize = { xs: 12, lg: 6 },
  required = false,
  helperText,
  disabled = false,
  allowedContentTypes = null,
  maxFileSizeMb = null,
}) {
  const validators = [];
  if (required) validators.push(requiredValidation);

  return (
    <Field
      name={name}
      validate={composeValidators(...validators)}
      render={({ input, meta }) => (
        <FileInputFieldContent
          input={input}
          meta={meta}
          gridSize={gridSize}
          label={label}
          required={required}
          helperText={helperText}
          disabled={disabled}
          allowedContentTypes={allowedContentTypes}
          maxFileSizeMb={maxFileSizeMb}
        />
      )}
    />
  );
}
